import React, { useEffect, useState } from 'react';
import Split from 'react-split';
import {
    makeStyles, Grid, useTheme,
    Button, Tooltip, useMediaQuery,
} from '@material-ui/core';
import {
    SortableContainer,
    SortableElement,
    arrayMove,
} from 'react-sortable-hoc';
import KeyStore from 'utils/KeyStore';
import { useQuery, useMutation } from '@apollo/client';
import { FetchPolicy } from 'utils/enum/Core';
import { TRAINING_SECTION } from 'utils/enum/General';
import Permission from 'utils/enum/Permissions';
import StringUtils from 'lib/StringUtils';
import ModalUtils from 'utils/ModalUtils';
import GeneralQuery from 'services/graphQL/query/GeneralQuery';
import GeneralMutation from 'services/graphQL/mutate/GeneralMutation';
import ButtonStyles from 'styles/theme/Button';
import SettingsStyle from 'styles/modules/businessIntelligence/SettingsStyle';
import ConfirmDialog from 'components/widgets/modal/ConfirmDialog';
import TrainingElementDialog from 'components/modules/settings/training/TrainingElementDialog';
import AddCategoryDialog from 'components/modules/settings/businessIntelligence/AddCategoryDialog';
import Search from 'components/widgets/Search';

// icons
import { ReactComponent as AddCircleOutlineIcon } from 'assets/addproduct.svg';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined';

const buttonStyles = makeStyles((theme) => ButtonStyles.getStyle(theme));
const useStyles = makeStyles((theme) => SettingsStyle.content(theme));

const TrainingElementSettings = () => {
    const keyStore = new KeyStore();
    const TRAINING_GLOBAL_SETTINGS_WRITE = keyStore.hasPermission(Permission.TRAINING_GLOBAL_SETTINGS_WRITE);
    const TRAINING_LIMITED_SETTINGS_MANAGE_CATEGORIES = keyStore.hasPermission(Permission.TRAINING_LIMITED_SETTINGS_MANAGE_CATEGORIES);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const classes = { ...useStyles(), ...buttonStyles() };
    const [state, setState] = useState({
        categories: [],
        selectedCategory: null,
        categoriesSearch: null,
        selectedElement: null,
        elementSearch: null,
        isCategoryPanelOpen: false,
        isElementPanelOpen: false,
        isDeletePromptVisible: false,
    });

    const {
        data: categoriesData,
        loading: loadingCategories,
        error: errorLoadingCategories,
        refetch: refetchCategories,
    } = useQuery(GeneralQuery.PULL_TRAINING_CATEGORIES, {
        variables: {
            isSettings: true,
        },
        fetchPolicy: FetchPolicy.NO_CACHE,
        notifyOnNetworkStatusChange: true,
    });

    useEffect(() => {
        if (errorLoadingCategories) {
            ModalUtils.errorMessage(errorLoadingCategories?.graphQLErrors);
            return;
        }

        if (!loadingCategories) {
            const categories = categoriesData?.pullTrainingCategories;
            if (categories) {
                let firstCategoryId = null;
                let firstCategoryElements = null;

                const categoryAvailability = categories.length > 0 && !state.selectedCategory;
                if (categoryAvailability) {
                    firstCategoryId = categories[0]?.trainingCategoryId;
                    firstCategoryElements = categories[0]?.elements ?? [];
                }

                setState((prevState) => ({
                    ...prevState,
                    categories,
                    ...(firstCategoryId ? { selectedCategory: firstCategoryId } : {}),
                    ...(firstCategoryElements?.length > 0 ? { selectedElement: firstCategoryElements[0].trainingElementId } : {}),
                }));
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingCategories, errorLoadingCategories]);

    const [addCategory, { loading: addingCategory }] = useMutation(GeneralMutation.ADD_TRAINING_CATEGORY, {
        onCompleted: (response) => {
            if (response) {
                setState((prevState) => ({
                    ...prevState,
                    categoriesSearch: null,
                    elementSearch: null,
                    isCategoryPanelOpen: false,
                    selectedCategory: response?.addTrainingCategory,
                }));

                refetchCategories();
                ModalUtils.successMessage(null, 'Category added sucessfully');
            }
        },
        onError: (error) => {
            ModalUtils.errorMessage(null, error);
        },
    });

    const [addElement, { loading: addingElement }] = useMutation(GeneralMutation.ADD_TRAINING_ELEMENT, {
        onCompleted: (response) => {
            if (response) {
                setState((prevState) => ({
                    ...prevState,
                    categoriesSearch: null,
                    elementSearch: null,
                    isElementPanelOpen: false,
                    selectedElement: response?.addTrainingElement,
                }));

                refetchCategories();
                ModalUtils.successMessage(null, 'Resource added sucessfully');
            }
        },
        onError: (error) => {
            ModalUtils.errorMessage(null, error);
        },
    });

    const [editCategory, { loading: editingCategory }] = useMutation(GeneralMutation.EDIT_TRAINING_CATEGORY, {
        onCompleted: (response) => {
            if (response) {
                setState((prevState) => ({
                    ...prevState,
                    categoriesSearch: null,
                    elementSearchSearch: null,
                    isDeletePromptVisible: false,
                    ...(state.isDeletePromptVisible ? { selectedCategory: null, selectedElement: null } : {}),
                    deleteSection: null,
                }));

                refetchCategories();
                ModalUtils.successMessage(null, 'Category edited sucessfully');
            }
        },
        onError: (error) => {
            ModalUtils.errorMessage(null, error);
        },
    });

    const [editElement, { loading: editingElement }] = useMutation(GeneralMutation.EDIT_TRAINING_ELEMENT, {
        onCompleted: (response) => {
            if (response) {
                setState((prevState) => ({
                    ...prevState,
                    categoriesSearch: null,
                    elementSearch: null,
                    isDeletePromptVisible: false,
                    isElementPanelOpen: false,
                    ...(state.isDeletePromptVisible ? { selectedElement: null } : {}),
                    deleteSection: null,
                }));

                refetchCategories();
                ModalUtils.successMessage(null, 'Resource edited sucessfully');
            }
        },
        onError: (error) => {
            ModalUtils.errorMessage(null, error);
        },
    });

    const [setCategoriesOrder] = useMutation(GeneralMutation.SET_TRAINING_CATEGORIES_ORDER, {
        onError: (error) => {
            ModalUtils.errorMessage(null, error);
        },
    });

    const [setElementsOrder] = useMutation(GeneralMutation.SET_TRAINING_ELEMENTS_ORDER, {
        onError: (error) => {
            ModalUtils.errorMessage(null, error);
        },
    });

    const actionFilter = (section, input) => {
        switch (section) {
        case TRAINING_SECTION.CATEGORIES:
            setState((prevState) => ({
                ...prevState,
                selectedCategory: null,
                categoriesSearch: input,
            }));

            break;
        case TRAINING_SECTION.RESOURCE:
            setState((prevState) => ({
                ...prevState,
                selectedElement: null,
                elementSearch: input,
            }));

            break;
        default:
            break;
        }
    };

    const setSelectedElement = (section, id) => {
        switch (section) {
        case TRAINING_SECTION.CATEGORIES:
            const elements = state.categories?.find((cat) => cat.trainingCategoryId === id)?.element ?? [];

            setState((prevState) => ({
                ...prevState,
                selectedElement: elements.length > 0 ? elements[0].trainingElementId : null,
                selectedCategory: id,
            }));

            break;
        case TRAINING_SECTION.RESOURCE:
            setState((prevState) => ({
                ...prevState,
                selectedElement: id,
            }));

            break;
        default:
            break;
        }
    };

    const toggleCreationDialog = (section, editing = false) => {
        if (section === TRAINING_SECTION.CATEGORIES) {
            setState((prevState) => ({
                ...prevState,
                isCategoryPanelOpen: !state.isCategoryPanelOpen,
                ...(!editing && !state.isCategoryPanelOpen ? { selectedCategory: null } : {}),
            }));

            return;
        }

        setState((prevState) => ({
            ...prevState,
            isElementPanelOpen: !state.isElementPanelOpen,
            ...(!editing && !state.isElementPanelOpen ? { selectedElement: null } : {}),
        }));
    };

    const toggleDeletePrompt = (section) => {
        let deleteDescription = null;
        if (section) {
            if (section === TRAINING_SECTION.CATEGORIES) deleteDescription = 'Removing this category will remove its associated resources, continue?';
            if (section === TRAINING_SECTION.RESOURCE) deleteDescription = 'Do you want to remove this resource, continue?';
        }

        setState((prevState) => ({
            ...prevState,
            isDeletePromptVisible: !state.isDeletePromptVisible,
            deleteSection: section,
            deleteDescription,
        }));
    };

    const onSaveElement = ({
        elementCategory,
        elementName,
        elementDescription,
        elementType,
        elementURL,
        matchingRoute,
    }) => {
        const elements = state.categories?.find((cat) => cat.trainingCategoryId === elementCategory)?.elements ?? [];
        setState((prevState) => ({
            ...prevState,
            selectedCategory: elementCategory,
        }));

        if (state.selectedElement) {
            editElement({
                variables: {
                    trainingElementId: state.selectedElement,
                    trainingCategoryId: elementCategory,
                    name: elementName,
                    description: elementDescription,
                    type: elementType,
                    url: elementURL,
                    matchingRoute,
                    active: true,
                },
            });

            return;
        }

        addElement({
            variables: {
                trainingCategoryId: elementCategory,
                name: elementName,
                description: elementDescription,
                type: elementType,
                url: elementURL,
                matchingRoute,
                order: elements.length + 1,
            },
        });
    };

    const onSaveCategory = (name) => {
        if (state.selectedCategory) {
            editCategory({
                variables: {
                    trainingCategoryId: state.selectedCategory,
                    name,
                    active: true,
                },
            });

            return;
        }

        addCategory({
            variables: {
                name,
                order: state.categories.length + 1,
            },
        });
    };

    const onDeleteAction = async () => {
        const {
            categories,
            deleteSection,
            selectedCategory,
            selectedElement,
        } = state;

        if (deleteSection === TRAINING_SECTION.CATEGORIES) {
            const name = categories?.find((cat) => cat.trainingCategoryId === selectedCategory)?.name;
            editCategory({
                variables: {
                    trainingCategoryId: selectedCategory,
                    name,
                    active: false,
                },
            });

            return;
        }

        if (deleteSection === TRAINING_SECTION.RESOURCE) {
            const elements = categories?.find((cat) => cat.trainingCategoryId === selectedCategory)?.elements;
            const currentElement = elements?.find((v) => v.trainingElementId === selectedElement);

            if (currentElement) {
                const {
                    trainingElementId,
                    name,
                    description,
                    type,
                    url,
                    matchingRoute,
                } = currentElement;

                editElement({
                    variables: {
                        trainingElementId,
                        trainingCategoryId: selectedCategory,
                        name,
                        description,
                        type,
                        url,
                        matchingRoute,
                        active: false,
                    },
                });
            }
        }
    };

    const onSortEnd = (oldIndex, newIndex, list, section) => {
        if (oldIndex !== newIndex) {
            const sorted = arrayMove(
                list,
                oldIndex,
                newIndex,
            );

            if (section === TRAINING_SECTION.CATEGORIES) {
                setState((prevState) => ({
                    ...prevState,
                    categories: sorted,
                }));

                setCategoriesOrder({
                    variables: {
                        input: sorted.map(({ trainingCategoryId }, index) => ({ trainingCategoryId, order: index + 1 })),
                    },
                });

                return;
            }

            if (section === TRAINING_SECTION.RESOURCE) {
                setState((prevState) => ({
                    ...prevState,
                    categories: prevState.categories.map((c) => {
                        if (c.trainingCategoryId === state.selectedCategory) {
                            const clone = { ...c };
                            clone.elements = sorted;

                            return clone;
                        }

                        return c;
                    }),
                }));

                setElementsOrder({
                    variables: {
                        input: sorted.map(({ trainingElementId }, index) => ({ trainingElementId, order: index + 1 })),
                    },
                });
            }
        }
    };

    const SortableItem = SortableElement(({
        id,
        element,
        sortIndex,
        isSelected,
        section,
    }) => {
        const isCategories = section === TRAINING_SECTION.CATEGORIES;

        return (
            <div
                className={isSelected ? classes.listItemSelected : classes.listItem}
                onClick={() => setSelectedElement(section, id)}
                key={sortIndex}
            >
                <span>{`${element.name}${!isCategories ? ` | ${element.type}` : ''}`}</span>
                {isSelected
                    && (
                        !isCategories
                        || (isCategories && (TRAINING_GLOBAL_SETTINGS_WRITE || TRAINING_LIMITED_SETTINGS_MANAGE_CATEGORIES)))
                    && (
                        <div>
                            <Tooltip title="Edit">
                                <span>
                                    <Button
                                        size="small"
                                        startIcon={<EditOutlinedIcon />}
                                        onClick={() => toggleCreationDialog(section, true)}
                                    />
                                </span>
                            </Tooltip>
                            <Tooltip title="Delete">
                                <span>
                                    <Button
                                        size="small"
                                        startIcon={<DeleteOutlineOutlinedIcon />}
                                        onClick={() => toggleDeletePrompt(section)}
                                    />
                                </span>
                            </Tooltip>
                        </div>
                    )}
            </div>
        );
    });

    const SortableList = SortableContainer(({ items, selected, section }) => (
        <div className={classes.list}>
            {items.map((element, index) => {
                const id = element.trainingCategoryId ?? element.trainingElementId;
                const isSelected = selected === id;

                return (
                    <SortableItem
                        key={`item-${section.toLowerCase()}-${index}`}
                        index={index}
                        sortIndex={index}
                        id={id}
                        element={element}
                        isSelected={isSelected}
                        section={section}
                    />
                );
            })}
        </div>
    ));

    const distanceInPixels = 5;
    const renderItems = (section) => {
        let list = [];
        let selected = null;
        let renderAddButton = true;

        switch (section) {
        case TRAINING_SECTION.CATEGORIES:
            list = StringUtils.isEmpty(state.categoriesSearch)
                ? state.categories?.map((cat) => ({ ...cat, name: cat.name?.toUpperCase() }))
                : state.categories?.filter((cat) => cat.name.toLowerCase().includes(state.categoriesSearch.toLowerCase()))
                    .map((cat) => ({ ...cat, name: cat.name?.toUpperCase() }));
            selected = state.selectedCategory;

            renderAddButton = TRAINING_GLOBAL_SETTINGS_WRITE || TRAINING_LIMITED_SETTINGS_MANAGE_CATEGORIES;
            break;
        default:
            selected = state.selectedElement;
            if (state.selectedCategory) {
                list = state.categories?.find((cat) => cat.trainingCategoryId === state.selectedCategory)?.elements ?? [];
                if (!StringUtils.isEmpty(state.elementSearch)) {
                    list = list.filter((el) => el.name.toLowerCase().includes(state.elementSearch.toLowerCase()));
                }
            }
            break;
        }

        const isAddDisabled = section === TRAINING_SECTION.RESOURCE && !state.selectedCategory;
        return (
            <div className={classes.container}>
                <div className={classes.label}>
                    {section}
                    {renderAddButton && (
                        <Button
                            className={isAddDisabled ? classes.addDisabled : ''}
                            disabled={isAddDisabled}
                            size="small"
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={() => toggleCreationDialog(section)}
                        />
                    )}
                </div>
                <div className={classes.boxSearch}>
                    <Search fullWidth onKeyDown={(input) => actionFilter(section, input)} />
                </div>
                <SortableList
                    axis="y"
                    distance={distanceInPixels}
                    items={list}
                    selected={selected}
                    section={section}
                    onSortEnd={({ oldIndex, newIndex }) => onSortEnd(oldIndex, newIndex, list, section)}
                />
            </div>
        );
    };

    const categoryDialogInitValue = state.selectedCategory
        ? state.categories.find((cat) => cat.trainingCategoryId === state.selectedCategory)?.name
        : '';

    return (
        <>
            {isMobile && (
                <Grid container className={classes.box}>
                    {renderItems(TRAINING_SECTION.CATEGORIES)}
                    {renderItems(TRAINING_SECTION.RESOURCE)}
                </Grid>
            )}
            {!isMobile && (
                <Grid container className={classes.box}>
                    <Split
                        sizes={[20, 80]}
                        minSize={0}
                        gutterSize={10}
                        gutterAlign="center"
                        direction="horizontal"
                        cursor="col-resize"
                        className={classes.splitter}
                    >
                        {renderItems(TRAINING_SECTION.CATEGORIES)}
                        {renderItems(TRAINING_SECTION.RESOURCE)}
                    </Split>
                </Grid>
            )}
            {state.isCategoryPanelOpen && (
                <AddCategoryDialog
                    initialValue={categoryDialogInitValue}
                    isSavingCategory={addingCategory}
                    onClose={() => toggleCreationDialog(TRAINING_SECTION.CATEGORIES)}
                    onSaveCategory={onSaveCategory}
                />
            )}
            {state.isElementPanelOpen && (
                <TrainingElementDialog
                    toggleDialog={() => toggleCreationDialog()}
                    onSave={onSaveElement}
                    isSavingElement={addingElement || editingElement}
                    categories={state.categories}
                    selectedCategory={state.selectedCategory}
                    selectedElement={state.selectedElement}
                    hasGlobalPermission={TRAINING_GLOBAL_SETTINGS_WRITE}
                />
            )}
            <ConfirmDialog
                title="Attention!"
                description={state.deleteDescription}
                open={state.isDeletePromptVisible}
                variant="outlined"
                titlePrimary="Yes"
                titleSecondary="Cancel"
                onClose={() => toggleDeletePrompt()}
                onClickSecondary={() => toggleDeletePrompt()}
                onClickPrimary={onDeleteAction}
                disablePrimaryButton={editingCategory || editingElement}
                disableSecondaryButton={editingCategory || editingElement}
            />
        </>
    );
};

export default TrainingElementSettings;
